export const arrowSize = 9;
export const bodyPadding = 5;
export const minArrowPadding = 5;
const cardPadding = 42;

/**
 * getScrollParent Get scroll parent element depends on target/node.
 *
 * @param   {HTMLElement} node        Target element to get scroll parent element.
 * @return  {HTMLElement}             Returns the parent element where it matched with target/node.
 */

export const getScrollParent = targetNode => {
  const regex = /(auto|scroll)/;

  const getStyle = (node, prop) => getComputedStyle(node).getPropertyValue(prop);
  const overflow = node => getStyle(node, 'overflow') + getStyle(node, 'overflow-y') + getStyle(node, 'overflow-x');
  const hasScroll = node => regex.test(overflow(node));

  const findScrollParent = node => {
    let target = node ?? null;

    while (target?.parentNode) {
      if (hasScroll(target)) return target;
      target = target.parentNode;
    }

    return document.scrollingElement || document.documentElement;
  };

  return findScrollParent(targetNode);
};

/**
 * getParentElementByClassName Get parent element depends on className string.
 *
 * @param   {HTMLElement} target        Target element to calculate positions.
 * @param   {string} parentClassName    String that represents a class name to find.
 * @return  {HTMLElement}               Returns the parent element where it matched with class name.
 */

const getParentElementByClassName = (target, parentClassName) => {
  let parent = target.parentElement;
  let parentTarget = window;

  while (parent) {
    if (parent.className.includes(parentClassName)) {
      parentTarget = parent;
      parent = undefined;
    } else {
      parent = parent.parentElement;
    }
  }

  return parentTarget;
};

/**
 * Get the mobile width from target.
 */
export const getMobileWidth = (target, parentElementClass = 'card') => {
  // If we don't have a "card" className in the closest parent element,
  // the width will be taken from the target element.
  const parentElement = getParentElementByClassName(target, parentElementClass);
  const mobileData = parentElement.getBoundingClientRect
    ? parentElement.getBoundingClientRect()
    : target.getBoundingClientRect();

  return mobileData.width - cardPadding;
};

/**
 * cross browser scroll positions
 */
export const getScrollTop = () => window.pageYOffset
  || document.documentElement.scrollTop
  || document.body.scrollTop
  || 0;

export const getScrollLeft = () => (
  window.pageXOffset || document.documentElement.scrollLeft || document.body.scrollLeft || 0
);

/**
* Sets tip max width safely for mobile
*/
export const getTooltipMaxWidth = () => ((typeof document !== 'undefined') ? document.documentElement.clientWidth - (bodyPadding * 2) : 1000);

/**
 * Evaluates if a variable is an non-null Object
 *
 * @param {any} obj variable to evaluate
 * @returns {Boolean}
 */
const isObject = obj => (!!obj && typeof obj === 'object');

/**
 * Similar to `Object.assign()`, but recursive
 *
 * @param {...Objects} targets The objects to merge
 * @returns {Object} The result of the merge
 */
export const deepMerge = (...targets) => (
  targets.reduce((result, rawTarget) => {
    if (typeof rawTarget !== 'object') return result;
    // Clone and convert to plain object
    const target = { ...rawTarget };
    if (!result) return target;
    const prevProps = Object.keys(result);
    const targetProps = Object.keys(target);
    // Get the list of unique props
    const allProps = new Set([...prevProps, ...targetProps]);
    const merged = {};

    allProps.forEach((prop) => {
      const prevVal = result[prop];
      const newVal = target[prop];

      // Prop only exist on one of the compared objects
      if (!(prop in target) || target[prop] === undefined) merged[prop] = prevVal;
      else if (!(prop in result) || result[prop] === undefined) merged[prop] = newVal;
      // Prop exist both of the compared objects and is are objects
      else if (isObject(newVal) && isObject(prevVal)) merged[prop] = deepMerge(prevVal, newVal);
      // Prop exist both of the compared objects but at least one of them isn't an object
      else merged[prop] = newVal;
    });

    return merged;
  }, null)
);

export const normalizeText = text => text.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

/**
 * Validates if an object has a method
 *
 * @param {Object} obj    - The object to evaluate
 * @param {string} method - The method name to validate
 * @returns {boolean}       The method exists or not
 */
export const methodExists = (obj, method) => Boolean(
  obj && obj instanceof Object && typeof obj[method] === 'function',
);

export const flutterWebViewReady = new Promise(
  resolve => window.addEventListener('flutterInAppWebViewPlatformReady', resolve),
);

export const sumBy = (collection, property) => (
  collection.reduce((accumulator, object) => accumulator + object[property], 0)
);
